/* Правка текста слайда на итоге «Слайдов с преимуществами» и «Карусели для поста» (APP-SPEC-5 §13.2, §14; docs/03-api.md §9.12).
   PATCH /app/orders/{id}/slides/{slide} с expected_revision и If-Match: сервер перерисовывает PNG тем же шаблоном без новой генерации
   и отвечает 202 с operation_order_id — подзаказом, у которого после done полный новый результат. Ключ идемпотентности
   и неизвестный исход переживают обновление страницы; новый ключ выдаётся только после известного исхода.
   Ядро — slide-edit.ts (оно же у движка шаблона): хук только держит его на владельца и исходный заказ и перерисовывает вид. */
import { useEffect, useReducer, useRef, useState } from 'react';
import type { SpeechOrder } from '../../api/speech';
import { createSlideEdit, type SlideEdit } from './slide-edit';

export { readEditChain } from './slide-edit';

/** Текущий итог — последняя подтверждённая правка (подзаказ) или исходный заказ. */
export function useSlideEdit(owner: number | undefined, root: SpeechOrder | null) {
  const rootId = root?.status === 'done' ? root.id : undefined;
  const [core, setCore] = useState<{ owner: number; root: number; edit: SlideEdit } | null>(null);
  const [, repaint] = useReducer((value: number) => value + 1, 0);
  const latest = useRef(root);
  useEffect(() => {
    latest.current = root;
  });
  useEffect(() => {
    if (!owner || !rootId) return;
    const edit = createSlideEdit({ owner, rootId, root: () => latest.current, onChange: repaint });
    setCore({ owner, root: rootId, edit });
    void edit.load();
    return () => {
      edit.dispose();
      setCore(current => (current?.edit === edit ? null : current));
    };
    /* ядро — на владельца и исходный заказ; новые снимки того же заказа его не пересоздают (root читается через latest) */
  }, [owner, rootId]);
  const edit = core && core.owner === owner && core.root === rootId ? core.edit : null;
  const view = edit?.view();
  const shown = view?.head ?? (root?.status === 'done' ? root : null);
  return {
    order: shown,
    pending: view?.chain?.pending ?? null,
    busy: !!view?.busy,
    error: view?.error ?? '',
    blocked: !!view?.blocked || !view?.chain,
    save: (slide: number, title: string, body: string) => (edit ? edit.save(slide, title, body) : Promise.resolve(false)),
    resume: () => (edit ? edit.resume() : Promise.resolve(false)),
  };
}
